/**
 * Turning a host's refusal to read the document into something a user can act on.
 *
 * This exists because of one behaviour that is otherwise baffling. In Excel, pressing a
 * ribbon button while a cell is being edited fires the button's handler but does NOT
 * take the cell out of edit mode -- so the handler's API call is refused with
 * `InvalidOperationInCellEditMode`, and stays refused however long it waits, because
 * edit mode does not time out. Clicking into the task pane commits the edit, and then
 * everything works.
 *
 * From the outside that looks like "Save is broken after I type something, but fine if
 * I click the pane first", which is impossible to guess at from a generic error. There
 * is no API to commit a cell edit from an add-in, so this cannot be fixed -- only
 * named accurately, which turns a mystery into a one-line instruction.
 */

/** The code Office puts on a Rich API refusal (Excel.run and friends). */
const CELL_EDIT_MODE = "InvalidOperationInCellEditMode";

/**
 * What the COMMON API says instead, and it says it for getFileAsync too -- so Save is
 * blocked mid-edit exactly as the identity read was. It is a generic "not valid right
 * now" rather than a named cause, which is why the wording it produces hedges: this is
 * overwhelmingly an unfinished edit in practice, but the code does not say so.
 */
const INVALID_IN_CONTEXT = "Invalid API call in the current context";

function errorCode(error: unknown): string | undefined {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string") return code;
  }
  return undefined;
}

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error ?? "");

/** True when the host refused because the user is part-way through an edit. */
export function isCellEditModeError(error: unknown): boolean {
  if (errorCode(error) === CELL_EDIT_MODE) return true;
  // Fallback for hosts that report it in the message rather than the code.
  return messageOf(error).includes(CELL_EDIT_MODE);
}

/**
 * True when the host would not give us the document, by either of the two names it
 * uses for it. The advice is the same in both cases, which is what matters to the
 * person reading it.
 */
export function isDocumentUnavailable(error: unknown): boolean {
  return isCellEditModeError(error) || messageOf(error).includes(INVALID_IN_CONTEXT);
}

/**
 * The one sentence worth showing when a host will not hand over the document. Written
 * once and used by both paths -- the identity read and Save -- because from the user's
 * side they are the same problem and have the same fix.
 */
export const FINISH_THE_EDIT =
  "The document is still being edited, so it could not be read. Press Enter to finish " +
  "the edit — or click anywhere in this pane — then try again.";

/**
 * What to tell the user when the document's Jylo details could not be read.
 *
 * Names the fix rather than the fault where we know it. Everything else gets a plain
 * "temporary, try again", which is honest -- we genuinely do not know.
 */
export function describeReadFailure(error: unknown, hostName: string): string {
  if (isCellEditModeError(error)) {
    return (
      `${hostName} is still editing a cell, so it will not let the add-in read this ` +
      "document. Press Enter to finish the edit — or click anywhere in this pane — then try again."
    );
  }
  if (isDocumentUnavailable(error)) {
    return FINISH_THE_EDIT;
  }
  return `Could not read this document's Jylo details just now. This is usually temporary — try again.`;
}
